import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { ColorPicker } from '@atlassian/color-picker/ui';
import { isReadable } from '@atlassian/color-picker/utils/color-conversion';
import { useBuiltInColorMap } from '@atlassian/color-picker/utils/use-built-in-color-map';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { resolveAdfColorForPicker } from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import { CARDS_EXTENSION_CONFIG_TEXT_ANALYTIC_SOURCE } from '../../../../constants';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { messages } from './messages';

type TextColorFieldProps = {
	backgroundColor: string;
	isCompanyHub: boolean;
	textColor: string;
	updateMainFormField: CardsActions['updateMainFormField'];
};

export const TextColorField = ({
	textColor,
	backgroundColor,
	isCompanyHub,
	updateMainFormField,
}: TextColorFieldProps): React.JSX.Element => {
	const { isSSRProductInDarkMode, branding } = useProductDependencies();
	const { hideDefaultColorPalette } = branding ?? {};
	const { textColorMap, emptyColorMap } = useBuiltInColorMap(isSSRProductInDarkMode);
	const theming = useHubTheming();
	const isV2 = theming?.isAdvancedThemingEnabled ?? false;
	const palette = isV2 ? theming?.palette : undefined;
	const primaryColor = isV2 ? theming?.primaryColor : undefined;
	const v2BrandMap = isV2 ? theming?.brandBackgroundColorMap : undefined;
	const readableTextHex =
		isV2 && primaryColor
			? isReadable('#FFFFFF', primaryColor)
				? '#FFFFFF'
				: '#000000'
			: '#000000';
	const resolvedBackgroundColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(backgroundColor, palette, 'primary.200', primaryColor)
			: backgroundColor;
	const resolvedTextColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(textColor, palette, 'primary.1000', readableTextHex, primaryColor)
			: textColor;
	const isReadableTextColor = (color: string) => isReadable(color, resolvedBackgroundColor);

	const toStoredColor = theming?.toStoredColor;
	const handleChange = useCallback(
		(color: string) => {
			const next = isV2 && toStoredColor ? toStoredColor(color) : color;
			updateMainFormField({ textColor: next });
		},
		[updateMainFormField, isV2, toStoredColor],
	);

	return (
		<Flex justifyContent="space-between" alignItems="center">
			<Text color="color.text">
				<FormattedMessage {...messages.textColorLabel} />
			</Text>
			<ColorPicker
				analyticsSource={CARDS_EXTENSION_CONFIG_TEXT_ANALYTIC_SOURCE}
				builtInColorMap={
					isV2 && v2BrandMap ? v2BrandMap : hideDefaultColorPalette ? emptyColorMap : textColorMap
				}
				isReadableColor={isReadableTextColor}
				onChange={handleChange}
				value={resolvedTextColor}
				pickerTitle={messages.textColorLabel}
				showCustomPalette={!!isCompanyHub}
			/>
		</Flex>
	);
};
